import React, { useEffect, useRef, useState } from "react";
import "./CreateEditLesson.css";
import { useNavigate, useParams } from "react-router-dom";
import CustomEditor from "../../../../components/custom-editor/CustomEditor";
import {
  CloudUpload,
  CrossIcon,
  DeleteIcon,
} from "../../../../icons/reactIcons";
import { useAppDispatch, useAppSelector } from "../../../../redux/store/hooks";
import {
  clearLessonDetails,
  createLessonThunk,
  updateLessonThunk, // Added
  fetchLessonByIdThunk,
  fetchWeekListThunk,
} from "../../../../redux/slices/lessonSlice";
import { fetchResourceCategoriesThunk } from "../../../../redux/slices/resourceSlice";
import { fetchCmsFileByIdThunk } from "../../../../redux/slices/fileSlice";
import { uploadCmsFile } from "../../../../apis/fileApis";
import Breadcrumb from "../../../../components/custom-breadcrumb/Breadcrumb";
import Loader from "../../../../components/custom-loader/Loader";
import { toast } from "react-toastify";

type LessonType = "video" | "document" | "website";
type AttachmentType = "Document" | "Video" | "Website";

interface Attachment {
  id: number | string; // Use string for existing IDs from API
  type: AttachmentType;
  file: File | null;
  fileId?: string;
  fileName?: string;
  url: string;
  duration: string;
}

/* ================= HELPERS ================= */

const toApiType = (type: AttachmentType): LessonType =>
  type.toLowerCase() as LessonType;

const toUiType = (type: LessonType): AttachmentType =>
  (type.charAt(0).toUpperCase() + type.slice(1)) as AttachmentType;

/* ================= COMPONENT ================= */

const CreateEditLesson: React.FC = () => {
  const navigate = useNavigate();
  const { lessonId } = useParams();
  const dispatch = useAppDispatch();

  const mainFileRef = useRef<HTMLInputElement>(null);
  const otherFileRef = useRef<HTMLInputElement>(null);
  const [activeOtherId, setActiveOtherId] = useState<number | string | null>(
    null,
  );

  const isEditMode = Boolean(lessonId);

  const { weekList, lessonDetails, loading } = useAppSelector(
    (state) => state.lesson,
  );
  const { categories } = useAppSelector((state) => state.resource);

  const [formData, setFormData] = useState({
    week: "",
    lessonName: "",
    shortDescription: "",
    resourceTags: [] as string[],
    details: "",
    buttonName: "",
  });

  const [mainLesson, setMainLesson] = useState<Attachment>({
    id: Date.now(),
    type: "Document",
    file: null,
    url: "",
    duration: "",
  });

  const [otherAttachments, setOtherAttachments] = useState<Attachment[]>([]);

  // Track deleted items for soft-delete API requirements
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState<string[]>(
    [],
  );
  const [deletedTagIds, setDeletedTagIds] = useState<string[]>([]);
  const [btnLoading, setBtnLoading] = useState(false);

  /* ================= EFFECTS ================= */

  const validateForm = () => {
    // Basic fields
    if (!formData.week) {
      toast.error("Week is required");
      return false;
    }

    if (!formData.lessonName.trim()) {
      toast.error("Lesson Name is required");
      return false;
    }

    if (!formData.shortDescription.trim()) {
      toast.error("Lesson Short Description is required");
      return false;
    }

    if (!formData.details.trim()) {
      toast.error("Lesson Details are required");
      return false;
    }

    if (!formData.buttonName.trim()) {
      toast.error("Button Name is required");
      return false;
    }

    if (formData.resourceTags.length === 0) {
      toast.error("At least one Resource Tag is required");
      return false;
    }

    // Main lesson validation
    if (mainLesson.type === "Document") {
      if (!mainLesson.file && !mainLesson.fileId) {
        toast.error("Main lesson document is required");
        return false;
      }
    }

    if (mainLesson.type === "Video") {
      if (!mainLesson.duration.trim()) {
        toast.error("Main video duration is required");
        return false;
      }
      if (!mainLesson.url.trim()) {
        toast.error("Main video URL is required");
        return false;
      }
    }

    if (mainLesson.type === "Website") {
      if (!mainLesson.url.trim()) {
        toast.error("Website URL is required");
        return false;
      }
    }

    // Other attachments validation
    for (let i = 0; i < otherAttachments.length; i++) {
      const att = otherAttachments[i];

      if (att.type === "Document") {
        if (!att.file && !att.fileId) {
          toast.error(`Attachment ${i + 1}: Document file is required`);
          return false;
        }
      }

      if (att.type === "Video") {
        if (!att.duration.trim()) {
          toast.error(`Attachment ${i + 1}: Video duration is required`);
          return false;
        }
        if (!att.url.trim()) {
          toast.error(`Attachment ${i + 1}: Video URL is required`);
          return false;
        }
      }

      if (att.type === "Website") {
        if (!att.url.trim()) {
          toast.error(`Attachment ${i + 1}: Website URL is required`);
          return false;
        }
      }
    }

    return true;
  };

  useEffect(() => {
    dispatch(fetchWeekListThunk());
    dispatch(fetchResourceCategoriesThunk());
    if (lessonId) dispatch(fetchLessonByIdThunk(lessonId));

    return () => {
      dispatch(clearLessonDetails());
    };
  }, [dispatch, lessonId]);

  /* ===== PREFILL EDIT MODE ===== */

  const getFileName = async (id: string) => {
    try {
      const result = await dispatch(fetchCmsFileByIdThunk(id)).unwrap();
      return result.maskName;
    } catch (error) {
      return "Unknown File";
    }
  };

  useEffect(() => {
    if (!lessonDetails || !isEditMode) return;

    const initializeFiles = async () => {
      setFormData({
        week: lessonDetails.weekId || "",
        lessonName: lessonDetails.name,
        shortDescription: lessonDetails.description,
        resourceTags: lessonDetails.categoriesMapped.map((c) => c.id),
        details: lessonDetails.lessonLongDescription,
        buttonName: lessonDetails.buttonName ?? "",
      });

      const mainDetail = lessonDetails.lessonTypeDetails?.[0];
      let mainFileName = "";
      if (mainDetail?.lessonFileId) {
        mainFileName = await getFileName(mainDetail.lessonFileId);
      }

      setMainLesson({
        id: "main",
        type: toUiType(lessonDetails.lessonType),
        file: null,
        fileId: mainDetail?.lessonFileId,
        fileName: mainFileName,
        url: mainDetail?.lessonUrl ?? "",
        duration: mainDetail?.lessonDuration ?? "",
      });

      const others = await Promise.all(
        (lessonDetails.otherAttachments || []).map(async (att, i) => {
          let name = "";
          if (att.attachmentFileId) {
            name = await getFileName(att.attachmentFileId);
          }
          return {
            id: att.attachmentFileId || `existing-${i}`,
            type: toUiType(att.attachmentType),
            file: null,
            fileId: att.attachmentFileId,
            fileName: name,
            url: att.attachmentUrl ?? "",
            duration: att.attachmentDuration ?? "",
          };
        }),
      );
      setOtherAttachments(others);
    };

    initializeFiles();
  }, [lessonDetails, isEditMode, dispatch]);

  /* ================= HANDLERS ================= */

  const handleMainChange = (field: keyof Attachment, value: any) => {
    setMainLesson((prev) => ({ ...prev, [field]: value }));
  };

  const handleOtherChange = (
    id: number | string,
    field: keyof Attachment,
    value: any,
  ) => {
    setOtherAttachments((prev) =>
      prev.map((a) => (a.id === id ? { ...a, [field]: value } : a)),
    );
  };

  const handleAddAttachment = () => {
    setOtherAttachments((prev) => [
      ...prev,
      { id: Date.now(), type: "Document", file: null, url: "", duration: "" },
    ]);
  };

  const handleRemoveAttachment = (id: number | string) => {
    const itemToRemove = otherAttachments.find((a) => a.id === id);
    // If it has a fileId, it exists on the server; track it for deletion
    if (itemToRemove?.fileId) {
      setDeletedAttachmentIds((prev) => [...prev, itemToRemove.fileId!]);
    }
    setOtherAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const handleRemoveTag = (catId: string) => {
    // If we are in edit mode, track the tag to be set to inactive
    if (isEditMode) {
      setDeletedTagIds((prev) => [...prev, catId]);
    }
    setFormData((p) => ({
      ...p,
      resourceTags: p.resourceTags.filter((id) => id !== catId),
    }));
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleEditorChange = (value: string) => {
    setFormData((prev) => ({ ...prev, details: value }));
  };

  /* ================= SUBMIT ================= */

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setBtnLoading(true);

    try {
      // 1. Upload Main File
      let finalMainFileId = mainLesson.fileId;
      if (mainLesson.type === "Document" && mainLesson.file) {
        finalMainFileId = await uploadCmsFile(mainLesson.file, "lesson");
      }

      // 2. Upload and Map Other Attachments
      const activeAttachments = await Promise.all(
        otherAttachments.map(async (att) => {
          let finalAttFileId = att.fileId;
          if (att.type === "Document" && att.file) {
            finalAttFileId = await uploadCmsFile(att.file, "lesson");
          }
          return {
            attachmentType: toApiType(att.type),
            attachmentUrl: att.url || undefined,
            attachmentDuration: att.duration || undefined,
            attachmentFileId: finalAttFileId || undefined,
            isActive: true,
          };
        }),
      );

      // Add soft-deleted attachments
      const deletedAttachments = deletedAttachmentIds.map((id) => ({
        attachmentFileId: id,
        isActive: false,
        attachmentType: "document" as const, // Type required by interface
      }));

      // 3. Map Categories
      const activeCategories = formData.resourceTags.map((id) => ({
        categoryId: id,
        isActive: true,
      }));

      const deletedCategories = deletedTagIds.map((id) => ({
        categoryId: id,
        isActive: false,
      }));

      // 4. Final Payload
      const payload = {
        weekId: formData.week,
        name: formData.lessonName,
        description: formData.shortDescription,
        lessonType: toApiType(mainLesson.type),
        lessonTypeDetails: [
          {
            lessonUrl: mainLesson.url || undefined,
            lessonDuration: mainLesson.duration || undefined,
            lessonFileId: finalMainFileId || undefined,
          },
        ],
        otherAttachments: [...activeAttachments, ...deletedAttachments],
        lessonLongDescription: formData.details,
        categories: [...activeCategories, ...deletedCategories],
        buttonName: formData.buttonName,
      };

      let res;
      if (isEditMode && lessonId) {
        res = await dispatch(updateLessonThunk({ lessonId, payload }));
      } else {
        res = await dispatch(createLessonThunk(payload));
      }

      if (
        createLessonThunk.fulfilled.match(res) ||
        updateLessonThunk.fulfilled.match(res)
      ) {
        navigate("/lessons");
      }
      setBtnLoading(false);
    } catch (error) {
      console.error("Submission error:", error);
      setBtnLoading(false);
    }
  };

  const breadcrumbItems = [
    { label: "Lessons", onClick: () => navigate("/lessons") },
    {
      label: isEditMode ? `Edit ${formData.lessonName}` : "Create Lesson",
      disabled: true,
    },
  ];

  if (loading) {
    return (
      <div className="lesson-details-container">
        <Breadcrumb items={breadcrumbItems} />
        <>
          <Loader />
        </>
      </div>
    );
  }

  return (
    <>
      <Breadcrumb items={breadcrumbItems} />
      <h1 className="page-title" style={{ marginTop: "12px" }}>
        {isEditMode ? "Edit Lesson" : "Create Lesson"}
      </h1>

      <div className="create-edit-lesson-container">
        <form className="create-edit-lesson-form" onSubmit={handleSubmit}>
          {/* Week & Lesson Name */}
          <div className="create-edit-lesson-form-row">
            <div className="create-edit-lesson-form-group left">
              <label>
                Week <span>*</span>
              </label>
              <select
                name="week"
                value={formData.week}
                onChange={handleChange}
                required
              >
                <option value="">Select Week</option>
                {weekList.map((week) => (
                  <option key={week.id} value={week.id}>
                    {week.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="create-edit-lesson-form-group right">
              <label>
                Lesson Name <span>*</span>
              </label>
              <input
                type="text"
                name="lessonName"
                value={formData.lessonName}
                onChange={handleChange}
                placeholder="Enter Lesson Name"
                required
              />
            </div>
          </div>

          {/* Short Description */}
          <div className="create-edit-lesson-form-group full">
            <label>
              Lesson Short Description <span>*</span>
            </label>
            <textarea
              name="shortDescription"
              value={formData.shortDescription}
              onChange={handleChange}
              placeholder="Enter Short Description"
              required
              rows={5}
            />
          </div>

          {/* MAIN LESSON SECTION */}
          <div className="create-edit-default-lessonType">
            <div className="create-edit-lesson-attachment-field lessonType">
              <label>
                Lesson Type <span className="required">*</span>
              </label>
              <select
                value={mainLesson.type}
                onChange={(e) => handleMainChange("type", e.target.value)}
              >
                <option value="Document">Document</option>
                <option value="Video">Video</option>
                <option value="Website">Website</option>
              </select>
            </div>

            <div className="create-edit-lesson-attachment-content">
              {mainLesson.type === "Document" && (
                <div className="create-edit-lesson-document">
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <label>
                      Upload File <span className="required">*</span>
                    </label>
                    <input
                      type="file"
                      hidden
                      ref={mainFileRef}
                      onChange={(e) =>
                        handleMainChange("file", e.target.files?.[0] || null)
                      }
                    />
                    <button
                      type="button"
                      onClick={() => mainFileRef.current?.click()}
                    >
                      Upload File <CloudUpload />
                    </button>
                  </div>
                  {(mainLesson.file || mainLesson.fileName) && (
                    <div className="create-edit-lesson-uploaded-file">
                      {mainLesson.file
                        ? mainLesson.file.name
                        : mainLesson.fileName}
                      <div
                        className="remove-icon"
                        onClick={() => {
                          handleMainChange("file", null);
                          handleMainChange("fileName", "");
                          handleMainChange("fileId", "");
                        }}
                      >
                        <CrossIcon />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {mainLesson.type === "Video" && (
                <div className="create-edit-lesson-video-fields">
                  <div className="video-time">
                    <label>
                      Duration <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="00:00"
                      value={mainLesson.duration}
                      onChange={(e) =>
                        handleMainChange("duration", e.target.value)
                      }
                    />
                  </div>
                  <div className="video-url">
                    <label>
                      Video URL <span className="required">*</span>
                    </label>
                    <input
                      type="url"
                      placeholder="Enter Video URL"
                      value={mainLesson.url}
                      onChange={(e) => handleMainChange("url", e.target.value)}
                    />
                  </div>
                </div>
              )}

              {mainLesson.type === "Website" && (
                <div className="create-edit-lesson-website-field">
                  <label>
                    Website URL <span className="required">*</span>
                  </label>
                  <input
                    type="url"
                    placeholder="Enter Website URL"
                    value={mainLesson.url}
                    onChange={(e) => handleMainChange("url", e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>

          {/* OTHER ATTACHMENTS */}
          <div className="create-edit-lesson-attachments-section">
            <h3>Attachments</h3>
            <input
              type="file"
              hidden
              ref={otherFileRef}
              onChange={(e) => {
                if (activeOtherId !== null) {
                  handleOtherChange(
                    activeOtherId,
                    "file",
                    e.target.files?.[0] || null,
                  );
                  setActiveOtherId(null);
                }
              }}
            />
            {otherAttachments.map((att) => (
              <div key={att.id} className="create-edit-lesson-attachment-row">
                <div className="create-edit-lesson-attachment-field">
                  <label>
                    Type <span className="required">*</span>
                  </label>
                  <select
                    value={att.type}
                    onChange={(e) =>
                      handleOtherChange(att.id, "type", e.target.value)
                    }
                  >
                    <option value="Document">Document</option>
                    <option value="Video">Video</option>
                    <option value="Website">Website</option>
                  </select>
                </div>

                <div className="create-edit-lesson-attachment-content">
                  {att.type === "Document" && (
                    <div className="create-edit-lesson-document">
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <label>
                          Upload File <span className="required">*</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            setActiveOtherId(att.id);
                            otherFileRef.current?.click();
                          }}
                        >
                          Upload File <CloudUpload />
                        </button>
                      </div>
                      {(att.file || att.fileName) && (
                        <div className="create-edit-lesson-uploaded-file">
                          {att.file ? att.file.name : att.fileName}
                          <div
                            className="remove-icon"
                            onClick={() => {
                              handleOtherChange(att.id, "file", null);
                              handleOtherChange(att.id, "fileName", "");
                              handleOtherChange(att.id, "fileId", "");
                            }}
                          >
                            <CrossIcon />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {att.type === "Video" && (
                    <div className="create-edit-lesson-video-fields">
                      <div className="video-time">
                        <label>
                          Duration <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="00:00"
                          value={att.duration}
                          onChange={(e) =>
                            handleOtherChange(
                              att.id,
                              "duration",
                              e.target.value,
                            )
                          }
                        />
                      </div>
                      <div className="video-url">
                        <label>
                          Video URL <span className="required">*</span>
                        </label>
                        <input
                          type="url"
                          placeholder="Enter Video URL"
                          value={att.url}
                          onChange={(e) =>
                            handleOtherChange(att.id, "url", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  )}

                  {att.type === "Website" && (
                    <div className="create-edit-lesson-website-field">
                      <label>
                        Website URL <span className="required">*</span>
                      </label>
                      <input
                        type="url"
                        placeholder="Enter Website URL"
                        value={att.url}
                        onChange={(e) =>
                          handleOtherChange(att.id, "url", e.target.value)
                        }
                      />
                    </div>
                  )}
                </div>

                <div className="create-edit-lesson-delete-attachment">
                  <DeleteIcon
                    style={{ marginTop: "2.4rem", cursor: "pointer" }}
                    onClick={() => handleRemoveAttachment(att.id)}
                  />
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={handleAddAttachment}
              className="create-edit-lesson-add-attachment-btn"
            >
              + Add more Attachments
            </button>
          </div>

          {/* TAGS */}
          <div className="create-edit-lesson-form-group full">
            <label>
              Select Resource Tags <span>*</span>
            </label>
            <div className="resource-tags-container">
              <select
                value=""
                onChange={(e) => {
                  const val = e.target.value;
                  if (val && !formData.resourceTags.includes(val)) {
                    setDeletedTagIds((prev) => prev.filter((id) => id !== val));
                    setFormData((prev) => ({
                      ...prev,
                      resourceTags: [...prev.resourceTags, val],
                    }));
                  }
                }}
              >
                <option value="">Select Resource Tag</option>
                {categories.map((cat) => (
                  <option
                    key={cat.id}
                    value={cat.id}
                    selected={formData.resourceTags.includes(cat.id)}
                    className={
                      formData.resourceTags.includes(cat.id)
                        ? "dropdown-option selected"
                        : "dropdown-option"
                    }
                  >
                    {cat.name}
                  </option>
                ))}
              </select>
              <div className="selected-tags">
                {formData.resourceTags.map((catId) => {
                  const category = categories.find((c) => c.id === catId);
                  return (
                    <div key={catId} className="tag-chip">
                      {category?.name}
                      <span
                        className="remove-tag"
                        onClick={() => handleRemoveTag(catId)}
                      >
                        ×
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="create-edit-lesson-form-group full">
            <label>
              Lesson Details <span>*</span>
            </label>
            <CustomEditor
              onChange={handleEditorChange}
              value={formData.details}
            />
          </div>

          <div className="create-edit-lesson-form-group full">
            <label>
              Button Name <span>*</span>
            </label>
            <input
              type="text"
              name="buttonName"
              value={formData.buttonName}
              onChange={handleChange}
              placeholder="Enter Button Name"
              required
            />
          </div>

          <div className="create-edit-lesson-form-actions">
            <button
              type="button"
              className="create-edit-lesson-cancel-btn"
              onClick={() => navigate("/lessons")}
            >
              Cancel
            </button>
            <button type="submit" className="create-edit-lesson-submit-btn">
              {btnLoading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default CreateEditLesson;
